import type { LoyaltyPointsEarnedProps } from '@engyalo/delivery-ui-components/lib/ui/components/LoyaltyPointsEarned/LoyaltyPointsEarned.types'
import type { DefaultProps } from '../../../interfaces'

/**
 * Default props for the loyalty-points pill above the cart CTA (COM-1397).
 *
 * The two data props are injected by `getLoyaltyPointsEarnedProps` in
 * `useCinnamonProps.ts` — `points` from `defaultSlice.loyaltyPointsToEarn`
 * (written by `useLoyaltyPointsPreflight`) and `message` from
 * `config.texts.cart.loyaltyPointsEarned` with the Spanish-mock fallback.
 * This file ships only the icon glyph: a storefront override can replace
 * the glyph or clear it to use the widget's own `'MdStar'` default.
 *
 * ⚠️ **No `points` or `message` default here.** The injector covers both
 * on every render, so an inert default would mask a wiring regression.
 * The widget hides when `points` is `null`, `undefined`, or `0`, so the
 * loading state ("show nothing") is handled by the initial slice value of
 * `null` — no skeleton, matches the COM-1397 scope.
 */
export const LoyaltyPointsEarnedDefault: DefaultProps<LoyaltyPointsEarnedProps> =
  {
    icon: 'MdStar',
    /*
     * Centre the pill in the cart footer and give it breathing room
     * above the Total-cart bar (COM-1527). The library ships it as
     * `inline-flex` with no horizontal centring of its own (COM-1396),
     * and cinnamon's `AppFooter` is a plain `block` container, so the
     * pill otherwise stacks against the left edge of the card.
     *
     * Three props do the work together: `display: flex` makes the pill
     * block-level so `margin: auto` can centre it; `width: fit-content`
     * keeps it at content width so the pill reads as a pill rather than
     * stretching to a full-width banner; `maxWidth: 100%` lets a long
     * localised message wrap inside the card instead of blowing past it.
     *
     * A storefront that wants a different placement overrides this prop
     * through SCS without a cinnamon release.
     */
    styles: {
      display: 'flex',
      width: 'fit-content',
      maxWidth: '100%',
      margin: '8px auto',
    },
  }

export default LoyaltyPointsEarnedDefault
